// components/find-investors/InvestorsList.jsx
import { useCallback, useMemo, useState, memo } from 'react'; // NOTE: use memo instead of React.memo
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import InvestorLogo from '../../../components/data/InvestorLogo';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import { Button } from '../../../components/ui/button';
import removeInvestorIcon from '../icons/remove-inv-from-dashboard.svg';
import { ExternalLink, Handshake, UserPlus, Loader2, CheckCircle2 } from 'lucide-react';
import InvestorAccessIcons from '../../../components/investor-display/InvestorAccessIcons';
import InvestorContactDetails from '../../../components/investor-display/InvestorContactDetails';
import InvestorIndustries from '../../../components/investor-display/InvestorIndustries';
import InvestorShortInfo from '../../../components/investor-display/InvestorShortInfo';
import { useMembershipAccess } from '../../../hooks/membership';
import {
  asString,
  buildInvestorProfilePath,
  getInvestorTypes,
  hasAnyInvestorAccessField,
  INVESTOR_CONTACT_KEYS,
  INVESTOR_SOCIAL_KEYS,
} from '../../../components/investor-display/helpers';
import { useCRM } from '../../../hooks/crm';

// ——— NEW: skeleton utils ———
const Shimmer = memo(({ className = '' }) => (
  <div className={`bg-stone-200 rounded animate-pulse ${className}`} />
));
const SkeletonCard = memo(function SkeletonCard() {
  return (
    <li className="bg-white border border-stone-300 shadow-md shadow-blue-50 rounded-md p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start mb-4 gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Shimmer className="w-6 h-6 rounded" />
          <div className="flex flex-col">
            <Shimmer className="h-5 w-48 rounded" />
          </div>
        </div>
        <Shimmer className="w-6 h-4 rounded" />
      </div>

      {/* Icons + deals */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className="inline-flex items-center justify-center w-5 h-5 align-middle">
              <Shimmer className="w-5 h-5 rounded" />
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Shimmer className="h-3.5 w-3.5 rounded" />
          <Shimmer className="h-4 w-28 rounded" />
        </div>
      </div>

      {/* Types */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Shimmer className="h-5 w-16 rounded" />
        <Shimmer className="h-5 w-12 rounded" />
        <Shimmer className="h-5 w-20 rounded" />
      </div>

      {/* Industries */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Shimmer className="h-6 w-20 rounded-full" />
        <Shimmer className="h-6 w-24 rounded-full" />
        <Shimmer className="h-6 w-16 rounded-full" />
        <Shimmer className="h-6 w-24 rounded-full" />
      </div>

      {/* Short info */}
      <div className="space-y-2">
        <Shimmer className="h-3 w-full rounded" />
        <Shimmer className="h-3 w-5/6 rounded" />
        <Shimmer className="h-3 w-2/3 rounded" />
      </div>
    </li>
  );
});

// ——— Investor card ———
const InvestorCard = memo(function InvestorCard({
  investor,
  investorInBoard,
  onAction,
  generateLink,
  canAddInvestor,
  canSeeSensitiveInfo,
  loadingInvestor,
}) {
  const { t } = useTranslation();

  // unique deals
  const { totalUniq } = useMemo(() => {
    const leadArr = Array.isArray(investor.as_lead_investor) ? investor.as_lead_investor.map(String) : [];
    const partArr = Array.isArray(investor.as_investor_participated) ? investor.as_investor_participated.map(String) : [];
    const leadSet = new Set(leadArr);
    const partSet = new Set(partArr);
    let both = 0;
    for (const id of leadSet) if (partSet.has(id)) both++;
    const totalUniq = leadSet.size + partSet.size - both;
    return { totalUniq };
  }, [investor.as_lead_investor, investor.as_investor_participated]);

  const hasSocialIcons = useMemo(
    () => hasAnyInvestorAccessField(investor, INVESTOR_SOCIAL_KEYS),
    [investor]
  );
  const hasFreeContactIcons = useMemo(
    () => !canSeeSensitiveInfo && hasAnyInvestorAccessField(investor, INVESTOR_CONTACT_KEYS),
    [canSeeSensitiveInfo, investor]
  );
  const investorTypes = useMemo(() => getInvestorTypes(investor), [investor]);

  const dealsText = useMemo(
    () => (totalUniq > 0 ? `${totalUniq.toLocaleString()} ${t('pageInvProf.records1')}` : ''),
    [totalUniq, t]
  );

  const nameText = investor?.name || '—';

  return (
    <li className="bg-white border border-stone-300 shadow-md shadow-blue-50 rounded-md p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start mb-4 gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <InvestorLogo
            id={investor?.id}
            country={investor?.country}
            className="flex-none w-6 h-6"
            alt={`${nameText} logo`}
            width={24}
            height={24}
            loading="lazy"
            decoding="async"
          />
          <h3 className="leading-tight">
            <Link
              to={generateLink(investor)}
              className="text-lg font-bold text-gray-900 hover:underline whitespace-normal break-words inline-block align-middle"
            >
              <span className="inline">{nameText}</span>
              <ExternalLink size={14} className="inline ml-1 align-baseline" />
            </Link>
          </h3>
        </div>

        {/* CRM button + flag */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {onAction && (
            <div className="relative group">
              {loadingInvestor === investor._id ? (
                <Button
                  variant="default"
                  size="xs"
                  wrap="nowrap"
                  disabled
                  iconLeft={<Loader2 className="animate-spin" />}
                  className="min-w-[120px] !bg-stone-100 !text-stone-950 !cursor-default"
                  aria-busy="true"
                >
                  adding…
                </Button>
              ) : investorInBoard ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="xs"
                    wrap="nowrap"
                    disabled
                    iconLeft={<CheckCircle2 />}
                    className="min-w-[120px] !bg-stone-100 !text-stone-950 !cursor-default"
                    title="Already in your CRM"
                  >
                    added
                  </Button>
                  <button
                    onClick={() => onAction(investor._id, 'remove')}
                    className="cursor-pointer text-stone-950 hover:text-red-700"
                    aria-label="Remove from CRM"
                    title="Remove from CRM"
                  >
                    <img src={removeInvestorIcon} alt="Remove" className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => canAddInvestor && onAction(investor._id, 'add')}
                  variant="default"
                  size="xs"
                  wrap="nowrap"
                  iconLeft={<UserPlus />}
                  className="min-w-[120px] !bg-stone-100 !text-stone-950"
                  disabled={!canAddInvestor}
                  aria-disabled={!canAddInvestor}
                  title={canAddInvestor ? 'Add to CRM' : 'Requires Startup/Professional'}
                >
                  to CRM
                </Button>
              )}
              {!canAddInvestor && (
                <div className="tooltip-content absolute top-1/2 -translate-y-1/2 left-full ml-2 px-3 py-2 bg-stone-700 text-xs text-white rounded-lg shadow-lg hidden group-hover:block z-10">
                  {t('pageFindInvestors.requires1')}
                </div>
              )}
            </div>
          )}
          <div className="w-6 h-4 flex-none overflow-hidden">
            <CountryFlag
              countryName={asString(investor?.country)}
              width={24}
              height={16}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>

      {(hasSocialIcons || hasFreeContactIcons || totalUniq > 0) && (
        <nav className="flex items-center justify-between mb-4" aria-label="Social and contacts">
          <InvestorAccessIcons
            investor={investor}
            socialMode={canSeeSensitiveInfo ? 'direct' : 'hint'}
            showContactIcons={!canSeeSensitiveInfo}
          />

          {totalUniq > 0 && (
            <div className="flex items-center gap-1.5">
              <Handshake size={14} className="text-stone-950" aria-hidden="true" />
              <a
                href={`${generateLink(investor)}#last-investments`}
                className="inline-flex underline underline-offset-4 decoration-blue-600 decoration-1 text-sm"
                aria-label={t('investorCard.viewRecentDeals')}
              >
                {dealsText}
              </a>
            </div>
          )}
        </nav>
      )}

      {canSeeSensitiveInfo && <InvestorContactDetails investor={investor} />}

      <div className="flex flex-wrap gap-2 mb-4">
        {investorTypes.map((tpe, i) => (
          <span key={`${tpe}-${i}`} className="text-sm">{tpe.trim()}</span>
        ))}
      </div>

      <InvestorIndustries industries={investor?.industries} t={t} />
      <InvestorShortInfo shortInfo={investor?.short_info} t={t} />
    </li>
  );
});

InvestorCard.propTypes = {
  investor: PropTypes.object.isRequired,
  investorInBoard: PropTypes.bool.isRequired,
  onAction: PropTypes.func,
  generateLink: PropTypes.func.isRequired,
  canAddInvestor: PropTypes.bool.isRequired,
  canSeeSensitiveInfo: PropTypes.bool.isRequired,
  loadingInvestor: PropTypes.string,
};

// ——— Main list ———
// NEW: added isLoading and pageSize
const InvestorsList = ({
  currentInvestors,
  isLoading = false,        // NEW
  pageSize = 10,            // NEW
}) => {
  const { i18n } = useTranslation();
  const {
    canAddInvestor,
    canSeeSensitiveInvestorInfo: canSeeSensitiveInfo,
  } = useMembershipAccess();
  const {
    selectedBoard,
    addInvestorToBoard,
    removeInvestorFromBoard,
    getInvestorColumnId,
  } = useCRM();

  const [loadingInvestor, setLoadingInvestor] = useState(null);

  const handleInvestorAction = useCallback(
    async (investorId, action) => {
      if (!selectedBoard || !canAddInvestor) return;

      setLoadingInvestor(investorId);
      const columnId = action === 'add'
        ? 1
        : getInvestorColumnId(investorId, selectedBoard.board_id);

      if (!columnId) {
        console.warn('No column found for remove action, or board has no columns');
        setLoadingInvestor(null);
        return;
      }

      try {
        if (action === 'add') {
          const investor = currentInvestors.find((item) => item?._id === investorId);
          await addInvestorToBoard({
            boardId: selectedBoard.board_id,
            columnId,
            investor,
          });
        } else {
          await removeInvestorFromBoard({
            boardId: selectedBoard.board_id,
            columnId,
            investorId,
          });
        }
      } catch (error) {
        console.error(`Error ${action}ing investor:`, error);
        alert(`Failed to ${action} investor.`);
      } finally {
        setLoadingInvestor(null);
      }
    },
    [
      addInvestorToBoard,
      canAddInvestor,
      currentInvestors,
      getInvestorColumnId,
      removeInvestorFromBoard,
      selectedBoard,
    ],
  );

  const generateLink = useCallback(
    investor => {
      return buildInvestorProfilePath({
        investor,
        language: i18n.language,
        inApp: true,
      });
    },
    [i18n.language],
  );

  // NEW: while loading, show a placeholder array of the required length; otherwise show real data
  const items = useMemo(
    () => (isLoading ? Array.from({ length: pageSize }) : currentInvestors),
    [isLoading, pageSize, currentInvestors]
  );

  return (
    <ul role="list" className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {items.map((investor, i) => {
        if (isLoading) {
          return <SkeletonCard key={`skeleton-${i}`} />;
        }

        const inBoard = selectedBoard?.columns.some(col =>
          col.investors.some(inv => inv.investor_id === investor._id),
        );

        return (
          <InvestorCard
            key={investor._id}
            investor={investor}
            investorInBoard={!!inBoard}
            onAction={handleInvestorAction}
            generateLink={generateLink}
            canAddInvestor={canAddInvestor}
            canSeeSensitiveInfo={canSeeSensitiveInfo}
            loadingInvestor={loadingInvestor}
          />
        );
      })}
    </ul>
  );
};

InvestorsList.propTypes = {
  currentInvestors: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,   // NEW
  pageSize: PropTypes.number,  // NEW
};

export default InvestorsList;
